import * as React from "react";
import { cn } from "../_internal/cn";
import type {
  TypeMetricHighlightProps,
  TypeMetricHighlightTrend,
} from "./MetricHighlightTypes";

/** Directional arrow glyph for the trend badge (decorative; `aria-hidden`). */
const TrendArrow = ({ direction }: { direction: "up" | "down" }) => (
  <svg
    className="seeds-metric-highlight-trend-arrow"
    viewBox="0 0 16 16"
    aria-hidden
    focusable={false}
  >
    {direction === "up" ? (
      <path
        d="M4.5 11.5 11.5 4.5M11.5 4.5H5.5M11.5 4.5V10.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M4.5 4.5 11.5 11.5M11.5 11.5H5.5M11.5 11.5V5.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const TrendBadge = ({
  direction = "up",
  value,
  ...rest
}: TypeMetricHighlightTrend) => (
  <span
    className={cn("seeds-metric-highlight-trend", {
      "seeds-metric-highlight-trend-down": direction === "down",
    })}
    // aria-label is prohibited on a bare span, so a labeled badge takes
    // role="img" — the label then names the badge as a whole (arrow + value).
    // Unlabeled, the badge stays a plain span whose value text reads normally;
    // role="img" without a name would be its own violation.
    role={rest["aria-label"] != null ? "img" : undefined}
    {...rest}
  >
    <TrendArrow direction={direction} />
    {value != null && (
      <span className="seeds-metric-highlight-trend-value">{value}</span>
    )}
  </span>
);

/**
 * MetricHighlight — a narrative primitive that spotlights a single metric: a
 * label, a large monospace value, an optional trend badge (a directional arrow
 * plus an optional number such as "240%"), and an optional chart supplied via
 * the `sparkline` slot (e.g. a `<SparklineChart />` from
 * `@sproutsocial/seeds-react-data-viz/sparkline`).
 *
 * Styled via `metric-highlight.css` (Seeds CSS custom properties); consumers
 * import the classes through `@sproutsocial/racine/css/components`. Layout flexes
 * across `appearance` (horizontal/vertical) and `size` (default/small); surface
 * overrides are done with standard `className` / `style`.
 */
const MetricHighlight = React.forwardRef<
  HTMLDivElement,
  TypeMetricHighlightProps
>(
  (
    {
      label,
      value,
      trend,
      sparkline,
      appearance = "horizontal",
      size = "default",
      container = true,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "seeds-metric-highlight",
          {
            "seeds-metric-highlight-vertical": appearance === "vertical",
            "seeds-metric-highlight-small": size === "small",
            "seeds-metric-highlight-bare": !container,
          },
          className
        )}
        {...props}
      >
        <div className="seeds-metric-highlight-metric">
          <p className="seeds-metric-highlight-label">{label}</p>
          <div className="seeds-metric-highlight-value-row">
            <p className="seeds-metric-highlight-value">{value}</p>
            {trend != null && <TrendBadge {...trend} />}
          </div>
        </div>
        {sparkline != null && (
          // Layout-only slot (bounded dimensions per horizontal/vertical/small)
          // for a consumer-supplied chart. It controls its own look; the slot
          // only sizes and centers it.
          <div className="seeds-metric-highlight-chart">{sparkline}</div>
        )}
      </div>
    );
  }
);

MetricHighlight.displayName = "MetricHighlight";

export default MetricHighlight;
